import React from 'react';
import { Box, Button, Container, IconButton, Stack, Typography } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import bandPhotos from './bandPhotos';

const googleFormUrl = 'https://docs.google.com/forms/d/e/1FAIpQLSehI4vArN5ygo-FCCiAbivxFM-aZVOot9DcdhgCKSN97-qVjw/viewform?usp=dialog';

const socialLinkStyles = {
  width: 48,
  height: 48,
  color: '#1b130f',
  border: '1px solid rgba(27, 19, 15, 0.42)',
  '&:hover': {
    color: '#f8f1e7',
    bgcolor: '#1b130f',
  },
};

function Contact() {
  const contactPhotos = [bandPhotos[0], bandPhotos[3], bandPhotos[8], bandPhotos[9]];

  return (
    <Box component="main">
      <Box
        component="section"
        sx={{
          position: 'relative',
          minHeight: { xs: 600, md: 620 },
          display: 'flex',
          alignItems: 'flex-end',
          overflow: 'hidden',
          bgcolor: '#0b0909',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gridTemplateRows: { xs: 'repeat(2, 1fr)', md: '1fr' },
          }}
        >
          {contactPhotos.map((photo) => (
            <Box
              key={photo.src}
              component="img"
              src={photo.src}
              alt=""
              width="160"
              height="120"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'saturate(1.08) contrast(1.06)',
              }}
            />
          ))}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: {
              xs: 'linear-gradient(180deg, rgba(11, 9, 9, 0.15) 0%, rgba(11, 9, 9, 0.74) 45%, #0b0909 100%)',
              md: 'linear-gradient(90deg, #0b0909 0%, rgba(11, 9, 9, 0.93) 42%, rgba(11, 9, 9, 0.25) 100%)',
            },
          }}
        />
        <Container
          maxWidth="xl"
          className="hero-reveal"
          sx={{
            position: 'relative',
            zIndex: 1,
            px: { xs: 2, sm: 3, md: 5 },
            pb: { xs: 7, md: 9 },
          }}
        >
          <Box sx={{ maxWidth: 720 }}>
            <Typography sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
              Dates fill up
            </Typography>
            <Typography
              component="h1"
              variant="h1"
              sx={{
                fontSize: { xs: '4.5rem', sm: '6rem', md: '7.5rem' },
                lineHeight: 0.86,
                textWrap: 'balance',
              }}
            >
              Book the band
            </Typography>
            <Typography
              sx={{
                color: 'text.primary',
                fontSize: { xs: '1.15rem', md: '1.4rem' },
                lineHeight: 1.6,
                maxWidth: 580,
                mt: 2,
              }}
            >
              Tell us the date, venue, and the kind of night you are planning. We will follow up with availability and next steps.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mt: 4, alignItems: { xs: 'stretch', sm: 'center' } }}
            >
              <Button
                component="a"
                href={googleFormUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="large"
                endIcon={<ArrowOutwardIcon />}
                sx={{ bgcolor: 'secondary.main', color: '#110c0a', '&:hover': { bgcolor: '#ffd06f' } }}
              >
                Start booking inquiry
              </Button>
              <Button
                component="a"
                href="mailto:music@malpractice.band"
                variant="outlined"
                size="large"
                startIcon={<EmailOutlinedIcon />}
                sx={{
                  color: 'text.primary',
                  borderColor: 'rgba(248, 241, 231, 0.55)',
                  '&:hover': {
                    borderColor: 'text.primary',
                    bgcolor: 'rgba(248, 241, 231, 0.08)',
                  },
                }}
              >
                Email us
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: '#f1e7d8', color: '#1b130f' }}>
        <Container
          maxWidth="xl"
          sx={{ px: { xs: 2, sm: 3, md: 5 }, py: { xs: 8, md: 11 } }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(280px, 0.55fr)' },
              gap: { xs: 6, md: 12 },
              alignItems: 'start',
            }}
          >
            <Box>
              <Typography sx={{ color: '#b43a25', fontWeight: 800, mb: 1 }}>
                Direct contact
              </Typography>
              <Typography
                component="h2"
                variant="h2"
                sx={{
                  color: '#17100d',
                  fontSize: { xs: '3.75rem', md: '5.5rem' },
                  lineHeight: 0.9,
                  textWrap: 'balance',
                }}
              >
                Prefer email? That works too.
              </Typography>
              <Typography
                component="a"
                href="mailto:music@malpractice.band"
                sx={{
                  minHeight: 44,
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: '#7f281b',
                  fontSize: { xs: '1.05rem', sm: '1.35rem' },
                  fontWeight: 800,
                  mt: 3,
                  textDecorationThickness: '2px',
                  textUnderlineOffset: '6px',
                  overflowWrap: 'anywhere',
                }}
              >
                music@malpractice.band
              </Typography>
            </Box>

            <Box sx={{ borderTop: '3px solid #e95d3f', pt: 3 }}>
              <Typography sx={{ color: '#2f241f', fontWeight: 700, mb: 2 }}>
                Follow Malpractice
              </Typography>
              <Typography sx={{ color: '#51423a', lineHeight: 1.7, mb: 3 }}>
                New show announcements, recent sets, and the occasional costume change.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <IconButton
                  aria-label="Facebook"
                  component="a"
                  href="https://www.facebook.com/MALPracticeShowband"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={socialLinkStyles}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton
                  aria-label="Instagram"
                  component="a"
                  href="https://www.instagram.com/malpractice_showband"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={socialLinkStyles}
                >
                  <InstagramIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Contact;
